import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormControl, ReactiveFormsModule } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import {
  DmyDateMode,
  digitsOnly,
  formatDmyFromDigits,
  localDateFromDmyParts,
  parseDmyPartsFromDisplay,
  validateDmyParts,
} from '../../../_helpers/dmy-date';

@Component({
  selector: 'app-dmy-date-field',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    TranslateModule,
  ],
  templateUrl: './dmy-date-field.component.html',
  providers: [
    {
      provide: ErrorStateMatcher,
      useFactory: createDmyErrorStateMatcher,
      deps: [DmyDateFieldComponent],
    },
  ],
})
export class DmyDateFieldComponent implements OnChanges {
  @Input() mode: DmyDateMode = 'birth';
  /** When true, empty input counts as valid (e.g. optional DOB). */
  @Input() allowEmpty = true;
  @Input() label = '';
  @Input() placeholder = 'DD/MM/YYYY';
  @Input() testId = '';
  /** Set true when parent submits (e.g. Save) to show errors without prior blur. */
  @Input() forceShowErrors = false;

  @Output() valueChange = new EventEmitter<Date | null>();

  /** Drives Material error state for `mat-error` visibility. */
  readonly fc = new FormControl<string>('', { nonNullable: true });

  /** Anchors `mat-datepicker` (Material requires an input with `[matDatepicker]`). */
  readonly pickerFc = new FormControl<Date | null>(null);

  /** Bound display string (DD/MM/YYYY with slashes). */
  displayValue = '';

  /** Last value received from parent via `[value]` (for blur restore when allowEmpty is false). */
  private parentValue: Date | null = null;

  @Input()
  set value(v: Date | null) {
    this.parentValue = v;
    this.displayValue = this.dateToDisplay(v);
    this.fc.setValue(this.displayValue, { emitEvent: false });
    this.syncPickerAnchorFromDate(v);
    this.syncControlErrors();
  }

  /** Calendar `max` — birth: today; schedule: end of allowed range. */
  get pickerMax(): Date {
    if (this.mode === 'birth') {
      const t = new Date();
      return new Date(t.getFullYear(), t.getMonth(), t.getDate());
    }
    return new Date(2100, 11, 31);
  }

  /** Calendar `min` — birth: distant past; schedule: 1900-01-01. */
  get pickerMin(): Date {
    if (this.mode === 'calendar') {
      return new Date(1900, 0, 1);
    }
    return new Date(1800, 0, 1);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['forceShowErrors']) {
      this.fc.updateValueAndValidity({ emitEvent: true });
    }
  }

  /** Returns a valid `Date` from the current display, or null if empty/invalid/incomplete. */
  getDateIfValid(): Date | null {
    const dig = digitsOnly(this.displayValue);
    if (dig.length === 0) return null;
    if (dig.length !== 8) return null;
    const parts = parseDmyPartsFromDisplay(this.displayValue);
    if (!parts || !validateDmyParts(this.mode, parts)) return null;
    return localDateFromDmyParts(parts);
  }

  /** Returns true if value is valid for submit (respects allowEmpty). */
  validate(): boolean {
    this.fc.markAsTouched();
    this.syncControlErrors();
    return this.isCurrentValueValid();
  }

  isCurrentValueValid(): boolean {
    const dig = digitsOnly(this.displayValue);
    if (dig.length === 0) return this.allowEmpty;
    if (dig.length !== 8) return false;
    const parts = parseDmyPartsFromDisplay(this.displayValue);
    return parts != null && validateDmyParts(this.mode, parts);
  }

  get errorMessageKey(): string {
    const dig = digitsOnly(this.displayValue);
    if (dig.length > 0 && dig.length < 8) {
      return 'Enter a complete date (DD/MM/YYYY)';
    }
    return this.mode === 'birth' ? 'Enter a valid date of birth' : 'Enter a valid date';
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const formatted = formatDmyFromDigits(input.value);
    this.displayValue = formatted;
    this.fc.setValue(formatted, { emitEvent: false });
    this.syncControlErrors();
    this.emitParsedValue();
    this.syncPickerAnchorFromDisplay();
  }

  onPickerDateChange(event: { value: Date | null }): void {
    const raw = event.value;
    if (!raw) {
      return;
    }
    const d = new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
    const parts = { day: d.getDate(), month: d.getMonth() + 1, year: d.getFullYear() };
    if (!validateDmyParts(this.mode, parts)) {
      return;
    }
    this.applyPickedLocalDate(d);
  }

  onBlur(): void {
    this.fc.markAsTouched();
    if (!this.allowEmpty && !this.isCurrentValueValid()) {
      this.displayValue = this.dateToDisplay(this.parentValue);
      this.fc.setValue(this.displayValue, { emitEvent: false });
      this.syncControlErrors();
      this.emitParsedValue();
      this.syncPickerAnchorFromDisplay();
    } else {
      this.syncControlErrors();
      this.syncPickerAnchorFromDisplay();
    }
  }

  private syncControlErrors(): void {
    if (this.isCurrentValueValid()) {
      this.fc.setErrors(null);
    } else {
      this.fc.setErrors({ dmyInvalid: true });
    }
  }

  onKeydown(event: KeyboardEvent): void {
    const allowed = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (allowed.includes(event.key)) return;
    if (event.ctrlKey || event.metaKey) return;
    if (event.key.length === 1 && event.key >= '0' && event.key <= '9') return;
    event.preventDefault();
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = event.clipboardData?.getData('text') ?? '';
    const digits = digitsOnly(text).slice(0, 8);
    this.displayValue = formatDmyFromDigits(digits);
    this.fc.setValue(this.displayValue, { emitEvent: false });
    this.syncControlErrors();
    this.emitParsedValue();
    this.syncPickerAnchorFromDisplay();
  }

  private applyPickedLocalDate(d: Date): void {
    this.displayValue = this.dateToDisplay(d);
    this.fc.setValue(this.displayValue, { emitEvent: false });
    this.pickerFc.setValue(d, { emitEvent: false });
    this.syncControlErrors();
    this.emitParsedValue();
  }

  private syncPickerAnchorFromDate(d: Date | null): void {
    this.pickerFc.setValue(d, { emitEvent: false });
  }

  private syncPickerAnchorFromDisplay(): void {
    const v = this.getDateIfValid();
    this.pickerFc.setValue(v, { emitEvent: false });
  }

  /**
   * Notifies parent when the value is cleared or when a full 8-digit date is entered.
   * Partial input does not emit — avoids wiping the field via two-way binding.
   */
  private emitParsedValue(): void {
    const dig = digitsOnly(this.displayValue);
    if (dig.length === 0) {
      this.valueChange.emit(null);
      this.pickerFc.setValue(null, { emitEvent: false });
      return;
    }
    if (dig.length !== 8) {
      return;
    }
    const parts = parseDmyPartsFromDisplay(this.displayValue);
    if (!parts || !validateDmyParts(this.mode, parts)) {
      this.valueChange.emit(null);
      return;
    }
    this.valueChange.emit(localDateFromDmyParts(parts));
  }

  private dateToDisplay(d: Date | null): string {
    if (!d || isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear());
    return formatDmyFromDigits(`${day}${month}${year}`);
  }
}

/** Shows `mat-error` only after blur / submit (`touched`) or when `forceShowErrors` is set — not while typing. */
function createDmyErrorStateMatcher(host: DmyDateFieldComponent): ErrorStateMatcher {
  return {
    isErrorState(control: AbstractControl | null): boolean {
      return !!(control && control.invalid && (control.touched || host.forceShowErrors));
    },
  };
}
